import 'bootstrap/dist/css/bootstrap.css';
import { useState, useEffect } from 'react';
import Spinner from '../../shared/spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, faTrash, faSave, faEdit,
    faAngleRight, faAngleLeft, faAngleDoubleRight, faAngleDoubleLeft, 
    faCalendarAlt, faSync, faExclamationTriangle, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../shared/customAlert';

// --- SERVICES ---
import ComissaoService from '../../services/comissaoService';
import EmpregadorService from '../../services/empregadorService';
import PromotoraService from '../../services/promotoraService';
import RegionalService from '../../services/regionalService';
import ComercialService from '../../services/comercialService';
import FinanciamentoService from '../../services/financiamentoService';

// --- INTERFACES ---

export interface ComissaoItemRegra {
    prazo: number;
    taxa: number;
    percentual: number; // Comissão Padrão
    percentualPremiacao?: number; // Nova: Comissão Diferida
}

export interface ComissaoRegraDTO {
    id?: number;
    tipoOrigem: 'PROMOTORA' | 'REGIONAL' | 'COMERCIAL';
    comissaoPerfil: { id: number | null; nome?: string }; 
    idOrigem: number;
    idsEmpregadores?: number[]; 
    idEmpregador?: number; 
    idTabelaFinanciamento: number;
    formaPagamento: string;
    itens: ComissaoItemRegra[];
    dataInicioVigencia: string;
    dataFimVigencia: string;
    ativo: boolean;
}

interface ComissaoListagemDTO {
    nomePerfil: string;
    nomePromotora: string;
    nomeTabela: string;
    formaPagamento: string;
    vigenciaInicio: string;
    vigenciaFim: string;
    nomesEmpregadores: string[];
    itens: ComissaoItemRegra[];
    
    idsRegras: number[];       
    idsEmpregadores: number[]; 
    idPerfil: number;
    idOrigem: number;
    idTabela: number;
    tipoOrigem: 'PROMOTORA' | 'REGIONAL' | 'COMERCIAL';
}

interface ComissaoPerfilDTO { id: number; nome: string; }
interface EmpregadorDTO { id: number; nome: string; }
interface OrigemDTO { id: number; nome: string; }
interface FinanciamentoResumoDTO { id: number; nome: string; }

function ComissaoRegrasComponent() {
  
  // --- ESTADOS ---
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loadingGlobal, setLoadingGlobal] = useState(false); 
  const [showModal, setShowModal] = useState(false);

  // Listas de Dados
  const [listaRegras, setListaRegras] = useState<ComissaoListagemDTO[]>([]);
  const [listaPerfis, setListaPerfis] = useState<ComissaoPerfilDTO[]>([]);
  const [listaEmpregadores, setListaEmpregadores] = useState<EmpregadorDTO[]>([]);
  const [listaOrigens, setListaOrigens] = useState<OrigemDTO[]>([]);
  const [listaTabelasFinanciamento, setListaTabelasFinanciamento] = useState<FinanciamentoResumoDTO[]>([]);

  // Loadings Específicos
  const [loadingOrigens, setLoadingOrigens] = useState(false);
  
  // MELHORIA: loadingEdicao agora guarda o ID do item sendo editado (ou null)
  const [loadingEdicaoId, setLoadingEdicaoId] = useState<number | null>(null); 
  // MELHORIA: loading interno do modal para quando abrir
  const [loadingModalData, setLoadingModalData] = useState(false);

  // Controle de Edição
  const [idsParaEdicao, setIdsParaEdicao] = useState<number[]>([]);

  // Dual List Box
  const [selectedLeft, setSelectedLeft] = useState<string[]>([]);
  const [selectedRight, setSelectedRight] = useState<string[]>([]);

  // Formulário
  const [formRegra, setFormRegra] = useState<ComissaoRegraDTO>({
    tipoOrigem: 'PROMOTORA',
    comissaoPerfil: { id: null },
    idOrigem: 0,
    idsEmpregadores: [], 
    idTabelaFinanciamento: 0,
    formaPagamento: 'FLAT', 
    itens: [], 
    dataInicioVigencia: new Date().toISOString().split('T')[0],
    dataFimVigencia: '2099-12-31',
    ativo: true
  });

  // Services
  const comissaoService = new ComissaoService();
  const empregadorService = new EmpregadorService();
  const promotoraService = new PromotoraService();
  const regionalService = new RegionalService();
  const comercialService = new ComercialService();
  const financiamentoService = new FinanciamentoService();

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  const carregarDadosIniciais = async () => {
    setLoadingGlobal(true);
    try {
        const [resRegras, resPerfis, resEmp, resFin] = await Promise.all([
            comissaoService.listarRegras(),
            comissaoService.listarPerfis(),
            empregadorService.listarTodosEmpregadoresAtivos(),
            financiamentoService.listarTodosFinanciamentos(1, 1000, {} as any)
        ]);

        setListaRegras(Array.isArray(resRegras.data) ? resRegras.data : []);
        
        const dadosPerfis = Array.isArray(resPerfis.data) ? resPerfis.data : resPerfis;
        if (Array.isArray(dadosPerfis)) setListaPerfis(dadosPerfis);

        const dadosEmp = Array.isArray(resEmp.data) ? resEmp.data : (Array.isArray(resEmp) ? resEmp : []);
        setListaEmpregadores(dadosEmp);

        const dadosFin = resFin.data.content || resFin.data || [];
        if (Array.isArray(dadosFin)) setListaTabelasFinanciamento(dadosFin);

    } catch (err: any) {
        setAlert({ message: 'Erro ao carregar dados iniciais.', type: 'error' });
    } finally {
        setLoadingGlobal(false);
    }
  };

  // --- BUSCA DINÂMICA DE ORIGENS ---
  const buscarOrigensPorTipo = async (tipo: string) => {
      setLoadingOrigens(true);
      setListaOrigens([]); 
      try {
          let response;
          let dadosFormatados: OrigemDTO[] = [];

          if (tipo === 'PROMOTORA') response = await promotoraService.listarTodasPromotorasAtivas();
          else if (tipo === 'REGIONAL') response = await regionalService.listarTodasRegionaisAtivas();
          else if (tipo === 'COMERCIAL') response = await comercialService.listarTodasComerciaisAtivas();

          const dadosRaw = response?.data || response || [];
          if (Array.isArray(dadosRaw)) {
              dadosFormatados = dadosRaw.map((item: any) => ({
                  id: item.id,
                  nome: item.nome || item.descricao || item.razaoSocial || `Item ${item.id}`
              }));
          }
          setListaOrigens(dadosFormatados);
      } catch (error) {
          setAlert({ message: 'Erro ao carregar lista de origens.', type: 'error' });
      } finally {
          setLoadingOrigens(false);
      }
  };

  // --- DUAL LIST BOX ---
  const availableEmpregadores = listaEmpregadores.filter(
      emp => !(formRegra.idsEmpregadores || []).includes(emp.id)
  );
  const chosenEmpregadores = listaEmpregadores.filter(
      emp => (formRegra.idsEmpregadores || []).includes(emp.id)
  );

  const handleMoveRight = () => {
      const idsToAdd = selectedLeft.map(idStr => parseInt(idStr));
      const newIds = [...(formRegra.idsEmpregadores || []), ...idsToAdd];
      setFormRegra({ ...formRegra, idsEmpregadores: newIds });
      setSelectedLeft([]);
  };

  const handleMoveLeft = () => {
      const idsToRemove = selectedRight.map(idStr => parseInt(idStr));
      const newIds = (formRegra.idsEmpregadores || []).filter(id => !idsToRemove.includes(id));
      setFormRegra({ ...formRegra, idsEmpregadores: newIds });
      setSelectedRight([]);
  };

  const handleMoveAllRight = () => {
      setFormRegra({ ...formRegra, idsEmpregadores: listaEmpregadores.map(e => e.id) });
      setSelectedLeft([]);
  };

  const handleMoveAllLeft = () => {
      setFormRegra({ ...formRegra, idsEmpregadores: [] });
      setSelectedRight([]);
  };

  // --- TABELA FINANCEIRA E ITENS ---
  const carregarPrazosDaTabela = async (idTabela: number) => {
      if (!idTabela || idTabela === 0) {
          setFormRegra(prev => ({ ...prev, idTabelaFinanciamento: 0, itens: [] }));
          return;
      }
      
      try {
          const response = await financiamentoService.listarFinanciamentoDTOPorId(idTabela.toString());
          const dadosApi = response.data;
          let itensMapeados: ComissaoItemRegra[] = [];

          if (dadosApi && Array.isArray(dadosApi.financiamentoTaxa)) {
              itensMapeados = dadosApi.financiamentoTaxa.map((item: any) => ({
                  prazo: item.prazoFim,
                  taxa: item.taxa,
                  percentual: item.percentComissao || 0, // Comissão Padrão
                  percentualPremiacao: item.percentPremiacao || 0 // Inicializa Diferida
              }));
          }
          itensMapeados.sort((a, b) => a.prazo - b.prazo);
          setFormRegra(prev => ({ ...prev, idTabelaFinanciamento: idTabela, itens: itensMapeados }));
      } catch (error) {
          setAlert({ message: 'Erro ao carregar prazos.', type: 'error' });
      }
  };

  // --- VALIDAÇÃO E ALTERAÇÃO DE VALORES (REGRA CMN) ---
  const handleItemChange = (index: number, campo: 'percentual' | 'percentualPremiacao', novoValor: number) => {
      // Regra Banco Central: Comissão Padrão não pode ser > 6%
      if (campo === 'percentual' && novoValor > 6) {
          setAlert({ 
              message: 'Atenção: Conforme Resolução CMN n° 4.935/2021, a Comissão Padrão (Correspondente) não pode exceder 6%.', 
              type: 'warning' 
          });
          novoValor = 6; // Trava o valor em 6
      }
      
      const novosItens = [...formRegra.itens];
      novosItens[index] = { ...novosItens[index], [campo]: novoValor };
      setFormRegra({ ...formRegra, itens: novosItens });
  };

  // --- AÇÕES ---

  const abrirModalNovaRegra = () => {
    const tipoPadrao = 'PROMOTORA';
    setFormRegra({
        tipoOrigem: tipoPadrao,
        comissaoPerfil: { id: null },
        idOrigem: 0,
        idsEmpregadores: [],
        idTabelaFinanciamento: 0,
        formaPagamento: 'FLAT',
        itens: [],
        dataInicioVigencia: new Date().toISOString().split('T')[0],
        dataFimVigencia: '2099-12-31',
        ativo: true
    });
    setIdsParaEdicao([]); 
    setSelectedLeft([]);
    setSelectedRight([]);
    setLoadingModalData(false); // Não precisa carregar nada extra para nova regra
    
    // Carrega a lista padrão ao abrir
    buscarOrigensPorTipo(tipoPadrao);
    setShowModal(true);
  };

  const salvarRegra = async () => {
    if (!formRegra.comissaoPerfil?.id) return setAlert({ message: 'Selecione o Tipo de Comissão.', type: 'warning' });
    if (!formRegra.idOrigem) return setAlert({ message: 'Selecione a Origem.', type: 'warning' });
    if (!formRegra.idsEmpregadores || formRegra.idsEmpregadores.length === 0) return setAlert({ message: 'Selecione ao menos um Empregador.', type: 'warning' });
    if (!formRegra.idTabelaFinanciamento) return setAlert({ message: 'Selecione a Tabela Financeira.', type: 'warning' });
    if (formRegra.itens.length === 0) return setAlert({ message: 'A tabela não gerou prazos.', type: 'warning' });

    const existeViolacao = formRegra.itens.some(i => i.percentual > 6);
    if (existeViolacao) {
        return setAlert({ message: 'Erro: Existem itens com Comissão Padrão acima de 6%. Ajuste antes de salvar.', type: 'error' });
    }

    try {
      setLoadingGlobal(true);

      if (idsParaEdicao.length > 0) {
          await comissaoService.excluirRegrasEmLote(idsParaEdicao);
      }

      const payload = { ...formRegra, idEmpregador: formRegra.idsEmpregadores?.[0] };
      await comissaoService.salvarRegra(payload as any);
      
      setAlert({ message: 'Regra salva com sucesso!', type: 'success' });
      setShowModal(false);
      setIdsParaEdicao([]);
      carregarDadosIniciais();
    } catch (err: any) {
      setLoadingGlobal(false);
      setAlert({ message: 'Erro ao salvar regra.', type: 'error' });
    }
  };

  const excluirRegraAgrupada = async (idsParaExcluir: number[]) => {
      if (!idsParaExcluir || idsParaExcluir.length === 0) return;
      if (!window.confirm(`Deseja excluir esta regra agrupada?`)) return;
      try {
          setLoadingGlobal(true);
          await comissaoService.excluirRegrasEmLote(idsParaExcluir);
          setAlert({ message: 'Regras excluídas com sucesso.', type: 'success' });
          carregarDadosIniciais();
      } catch (err) {
          setLoadingGlobal(false);
          setAlert({ message: 'Erro ao excluir regras.', type: 'error' });
      }
  };

  // --- AÇÃO DE EDITAR OTIMIZADA ---
  const editarRegraAgrupada = async (item: ComissaoListagemDTO) => {
      if (!item.idsRegras || item.idsRegras.length === 0) {
          return setAlert({ message: 'Erro: Registro sem identificador para edição.', type: 'error' });
      }

      // 1. Identifica o ID e seta loading APENAS para este item
      const idReferencia = item.idsRegras[0];
      setLoadingEdicaoId(idReferencia); 
      
      // 2. Abre o Modal IMEDIATAMENTE e mostra loading interno
      setShowModal(true);
      setLoadingModalData(true);

      try {
          // 3. Busca os dados agora que o modal já abriu
          const response = await comissaoService.buscarRegraPorId(idReferencia);
          const dadosBackend = response.data; 

          // 4. Carrega dependências
          await buscarOrigensPorTipo(dadosBackend.tipoOrigem);

          const dataParaInput = (d: string) => {
              if(!d) return '';
              try {
                  if (d.includes('-')) return d; 
                  const partes = d.split('/');
                  if(partes.length === 3) return `${partes[2]}-${partes[1]}-${partes[0]}`;
                  return d;
              } catch { return ''; }
          };

          const idsSeguros = item.idsEmpregadores || [];
          const itensTratados = (dadosBackend.itens || []).map((i: any) => ({
              ...i,
              percentualPremiacao: i.percentualPremiacao !== undefined ? i.percentualPremiacao : 0
          }));

          setFormRegra({
              id: undefined, 
              tipoOrigem: dadosBackend.tipoOrigem,
              comissaoPerfil: dadosBackend.comissaoPerfil || { id: dadosBackend.idTipoComissao || item.idPerfil }, 
              idOrigem: dadosBackend.idOrigem,
              idTabelaFinanciamento: dadosBackend.idTabelaFinanciamento,
              formaPagamento: dadosBackend.formaPagamento,
              idsEmpregadores: idsSeguros,
              itens: itensTratados,
              dataInicioVigencia: dataParaInput(dadosBackend.dataInicioVigencia),
              dataFimVigencia: dataParaInput(dadosBackend.dataFimVigencia),
              ativo: true
          });

          setSelectedRight(idsSeguros.map(String));
          setSelectedLeft([]);
          setIdsParaEdicao(item.idsRegras);

      } catch (error) {
          console.error(error);
          setAlert({ message: 'Erro ao buscar dados para edição.', type: 'error' });
          setShowModal(false); // Fecha se der erro
      } finally {
          // 5. Finaliza loadings
          setLoadingModalData(false);
          setLoadingEdicaoId(null); // Libera o botão
      }
  };

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      
      <div className="position-relative m-md-3">
        {loadingGlobal ? <Spinner loading={loadingGlobal} /> : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-body">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5>Parâmetros &gt; Regras de Comissão</h5>
                <button className="btn btn-success" onClick={abrirModalNovaRegra}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} /> Nova Regra
                </button>
              </div>

              <div className="table-responsive mt-3">
                <table className="table table-bordered table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Perfil / Origem</th>
                      <th>Empregadores Vinculados</th>
                      <th>Tabela / Condição</th>
                      <th className="text-center">Vigência</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(listaRegras) && listaRegras.length > 0 ? (
                        listaRegras.map((item, index) => {
                          // Verifica se este item específico está carregando
                          const isThisLoading = loadingEdicaoId === item.idsRegras[0];
                          
                          return (
                            <tr key={index}>
                              <td>
                                  <div className="fw-bold text-primary">{item.nomePerfil}</div>
                                  <small className="text-muted text-uppercase">{item.nomePromotora} ({item.tipoOrigem || '-'})</small>
                              </td>
                              <td>
                                  <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                                      {item.nomesEmpregadores && item.nomesEmpregadores.length > 0 ? (
                                          item.nomesEmpregadores.map((nome, i) => (
                                              <span key={i} className="badge bg-secondary me-1 mb-1 text-white border border-light">
                                                  {nome}
                                              </span>
                                          ))
                                      ) : (
                                          <span className="text-muted small">-</span>
                                      )}
                                  </div>
                              </td>
                              <td>
                                  <div className="fw-bold">{item.nomeTabela}</div>
                                  <span className="badge bg-info text-dark">{item.formaPagamento}</span>
                              </td>
                              <td className="text-center small align-middle">
                                  <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-1"/>
                                  {item.vigenciaInicio} <br/> até <br/> {item.vigenciaFim}
                              </td>
                              <td className="text-center align-middle">
                                  <div className="btn-group">
                                      <button 
                                          className="btn btn-sm btn-outline-primary" 
                                          onClick={() => editarRegraAgrupada(item)} 
                                          disabled={loadingEdicaoId !== null} // Desabilita todos se um estiver carregando
                                          title="Editar"
                                      >
                                          {isThisLoading ? <FontAwesomeIcon icon={faSync} spin/> : <FontAwesomeIcon icon={faEdit} />}
                                      </button>
                                      <button 
                                          className="btn btn-sm btn-outline-danger" 
                                          onClick={() => excluirRegraAgrupada(item.idsRegras)} 
                                          title="Excluir"
                                          disabled={loadingEdicaoId !== null}
                                      >
                                          <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                  </div>
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                        <tr><td colSpan={5} className="text-center p-4 text-muted">Nenhuma regra cadastrada.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {showModal && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
                <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-primary text-white">
                            <h5 className="modal-title">
                                {idsParaEdicao.length > 0 ? 'Editar Regra' : 'Nova Regra'}
                            </h5>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                        </div>
                        <div className="modal-body position-relative">
                            
                            {/* SPINNER DE CARREGAMENTO INTERNO DO MODAL */}
                            {loadingModalData && (
                                <div className="position-absolute w-100 h-100 top-0 start-0 bg-white d-flex flex-column justify-content-center align-items-center" style={{zIndex: 10, opacity: 0.9}}>
                                    <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}></div>
                                    <div className="mt-3 fw-bold text-muted">Carregando dados da regra...</div>
                                </div>
                            )}

                            {/* CONTEÚDO DO FORMULÁRIO */}
                            <div>
                                {/* BLOCO 1 - Definição */}
                                <h6 className="text-muted border-bottom pb-2 mb-3">1. Definição da Regra</h6>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Tipo de Comissão <span className="text-danger">*</span></label>
                                        <select 
                                            className="form-select" 
                                            value={formRegra.comissaoPerfil?.id || ''} 
                                            onChange={e => setFormRegra({ ...formRegra, comissaoPerfil: { id: parseInt(e.target.value) } })}
                                        >
                                            <option value="">Selecione...</option>
                                            {listaPerfis.map(perfil => (
                                                <option key={perfil.id} value={perfil.id}>{perfil.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Tipo Origem</label>
                                        <select 
                                            className="form-select" 
                                            value={formRegra.tipoOrigem} 
                                            onChange={e => {
                                                const novoTipo = e.target.value as any;
                                                setFormRegra({...formRegra, tipoOrigem: novoTipo, idOrigem: 0});
                                                buscarOrigensPorTipo(novoTipo); 
                                            }}
                                        >
                                            <option value="PROMOTORA">Promotora</option>
                                        </select>
                                    </div>
                                    <div className="col-md-5">
                                        <label className="form-label">
                                            Origem {loadingOrigens && <FontAwesomeIcon icon={faSync} spin className="text-muted ms-1"/>} <span className="text-danger">*</span>
                                        </label>
                                        <select 
                                            className="form-select" 
                                            value={formRegra.idOrigem} 
                                            onChange={e => setFormRegra({...formRegra, idOrigem: parseInt(e.target.value)})}
                                            disabled={loadingOrigens || listaOrigens.length === 0}
                                        >
                                            <option value="0">{loadingOrigens ? 'Carregando...' : 'Selecione...'}</option>
                                            {listaOrigens.map(o => (
                                                <option key={o.id} value={o.id}>{o.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* BLOCO 2 - Empregadores */}
                                <h6 className="text-muted border-bottom pb-2 mb-3 mt-4">2. Empregadores</h6>
                                <div className="row align-items-center bg-light p-3 rounded border">
                                    <div className="col-md-5">
                                        <label className="fw-bold mb-1">Disponíveis</label>
                                        <select 
                                            multiple className="form-control" style={{ height: '150px' }}
                                            value={selectedLeft}
                                            onChange={e => setSelectedLeft(Array.from(e.target.selectedOptions, o => o.value))}
                                        >
                                            {availableEmpregadores.map(emp => (
                                                <option key={emp.id} value={emp.id.toString()}>{emp.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex flex-column gap-2">
                                        <button className="btn btn-secondary btn-sm w-100" onClick={handleMoveRight} disabled={selectedLeft.length === 0}><FontAwesomeIcon icon={faAngleRight} /></button>
                                        <button className="btn btn-secondary btn-sm w-100" onClick={handleMoveAllRight} disabled={availableEmpregadores.length === 0}><FontAwesomeIcon icon={faAngleDoubleRight} /></button>
                                        <button className="btn btn-secondary btn-sm w-100" onClick={handleMoveLeft} disabled={selectedRight.length === 0}><FontAwesomeIcon icon={faAngleLeft} /></button>
                                        <button className="btn btn-secondary btn-sm w-100" onClick={handleMoveAllLeft} disabled={chosenEmpregadores.length === 0}><FontAwesomeIcon icon={faAngleDoubleLeft} /></button>
                                    </div>
                                    <div className="col-md-5">
                                        <label className="fw-bold mb-1 text-success">Selecionados</label>
                                        <select 
                                            multiple className="form-control border-success" style={{ height: '150px' }}
                                            value={selectedRight}
                                            onChange={e => setSelectedRight(Array.from(e.target.selectedOptions, o => o.value))}
                                        >
                                            {chosenEmpregadores.map(emp => (
                                                <option key={emp.id} value={emp.id.toString()}>{emp.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* BLOCO 3 - Configuração Financeira */}
                                <h6 className="text-muted border-bottom pb-2 mb-3 mt-4 d-flex justify-content-between">
                                    <span>3. Configuração Financeira</span>
                                    <span className="badge bg-warning text-dark align-self-center">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        Norma CMN n° 4.935: Comissão Padrão Máx. 6%
                                    </span>
                                </h6>
                                <div className="row mb-3">
                                    <div className="col-md-8">
                                        <label className="form-label fw-bold">Tabela:</label>
                                        <select 
                                            className="form-select" 
                                            value={formRegra.idTabelaFinanciamento} 
                                            onChange={e => carregarPrazosDaTabela(parseInt(e.target.value))}
                                        >
                                            <option value="0">-- Selecione --</option>
                                            {listaTabelasFinanciamento.map(t => ( <option key={t.id} value={t.id}>{t.nome}</option> ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Pagamento:</label>
                                        <select className="form-select" value={formRegra.formaPagamento} onChange={e => setFormRegra({...formRegra, formaPagamento: e.target.value})}>
                                            <option value="FLAT">Flat (À Vista)</option>
                                            <option value="DIFERIDA">Diferida</option>
                                        </select>
                                    </div>
                                </div>

                                {formRegra.itens.length > 0 && (
                                    <div className="mt-3 p-3 bg-light border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <table className="table table-sm table-bordered bg-white mb-0">
                                            <thead className="table-secondary position-sticky top-0" style={{zIndex: 1}}>
                                                <tr>
                                                    <th className="text-center align-middle" style={{ width: '10%' }}>Prazo</th>
                                                    <th className="text-center align-middle" style={{ width: '10%' }}>Taxa</th>
                                                    
                                                    {/* COLUNA PADRÃO (Limitada a 6%) */}
                                                    <th className="text-center align-middle" style={{ width: '40%' }}>
                                                        Comissão Padrão <br/>
                                                        <small className="text-danger fw-light" style={{fontSize: '0.7em'}}>
                                                            (Max 6% - CMN 4.935)
                                                        </small>
                                                    </th>
                                                    
                                                    {/* COLUNA PREMIAÇÃO (Nova) */}
                                                    <th className="text-center align-middle" style={{ width: '40%' }}>
                                                        Comissão Diferida
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formRegra.itens.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center align-middle fw-bold">{item.prazo}x</td>
                                                        <td className="text-center align-middle text-muted">{item.taxa}%</td>
                                                        
                                                        {/* INPUT PADRÃO */}
                                                        <td className="text-center align-middle">
                                                            <div className="input-group input-group-sm">
                                                                <input type="number" step="0.01" className="form-control text-center fw-bold text-primary" 
                                                                    value={item.percentual}
                                                                    max="6" // HTML constraint
                                                                    onChange={e => handleItemChange(index, 'percentual', parseFloat(e.target.value))}
                                                                />
                                                                <span className="input-group-text">%</span>
                                                            </div>
                                                            {item.percentual > 6 && (
                                                                <small className="text-danger d-block mt-1">
                                                                    <FontAwesomeIcon icon={faExclamationTriangle} /> Excede limite legal
                                                                </small>
                                                            )}
                                                        </td>

                                                        {/* INPUT PREMIAÇÃO */}
                                                        <td className="text-center align-middle">
                                                            <div className="input-group input-group-sm">
                                                                <input type="number" step="0.01" className="form-control text-center text-success" 
                                                                    value={item.percentualPremiacao || 0}
                                                                    onChange={e => handleItemChange(index, 'percentualPremiacao', parseFloat(e.target.value))}
                                                                />
                                                                <span className="input-group-text">%</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <label>Início Vigência</label>
                                        <input type="date" className="form-control" value={formRegra.dataInicioVigencia} onChange={e => setFormRegra({...formRegra, dataInicioVigencia: e.target.value})} />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Fim Vigência</label>
                                        <input type="date" className="form-control" value={formRegra.dataFimVigencia} onChange={e => setFormRegra({...formRegra, dataFimVigencia: e.target.value})} />
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="btn btn-success" onClick={salvarRegra} disabled={loadingGlobal || loadingModalData}>
                                {loadingGlobal ? <><FontAwesomeIcon icon={faSync} spin/> Salvando...</> : <><FontAwesomeIcon icon={faSave} /> Salvar Regra</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

      </div>
    </>
  );
}

export default ComissaoRegrasComponent;